import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, formatDate, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { PHARMACY_PRODUCT_TYPE } from '../../../../../constants/dropdown';
import { PHARMACY_TYPE_MENUS } from '../../../../../constants/dropdown';
import { WarehouseStockTransferRequestDetailForm } from './WarehouseStockTransferRequestDetailForm';

const TABLE_HEADER = [
  {
    header: 'Product Type',
    accessor: 'product_type'
  },
  {
    header: 'From Location',
    accessor: (row) => row?.from_location?.warehouse_name
  },
  {
    header: 'To Location',
    accessor: (row) => row?.to_location?.warehouse_name
  },
  {
    header: 'Indent No',
    accessor: 'indent_no'
  },
  {
    header: 'Created At',
    accessor: (row) => `${getCurrentDateTime('date', row.createdAt)}`
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const initialWarehouseStockDataObj = {
  data: [],
  loading: false
};

export const WarehouseStockTransferRequest = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [warehouseStockTransferRequestDataObj, setWarehouseStockTransferRequestDataObj] =
    useState(initialWarehouseStockDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [input, setInput] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [warehouse, setWareHosue] = useState([]);
  const [addDetailsDialogue, setAddDetailsDialogOpen] = useState(false);
  const [accountLedger, setAccountLedger] = useState([]);
  const [productDropdown, setProductDropdown] = useState([]);
  const [productDropdowns, setProductDropdowns] = useState([]);
  const [storeProductDropdown, setStoreProductDropdown] = useState([]);
  const [filteredServiceList, setFilteredServiceList] = useState([]);
  const [wardDropdown, setWardDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const methods = useForm({
    resolver: yupResolver(Yup.object().shape({})),
    defaultValues: {
      product_type: '',
      date: new Date().toISOString().split('T')[0],
      invoice_date: new Date().toISOString().split('T')[0],
      bill: {
        out_sourced: 'No',
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;
  const column = watch('view_column');

  const BILL_TABLE_HEADER = [
    {
      header: 'Product Name',
      accessor: (row) => row?.product_name || row?.product?.main?.name || row?.store_product?.name
    },
    column ? { header: 'Product ID', accessor: '' } : null,
    { header: 'Qty', accessor: 'requested_qty' },
    { header: 'Stock', accessor: 'closing_qty' },
    { header: 'Batch No', accessor: 'batch_no' },
    { header: 'Exp Date', accessor: (row)=>formatDate(row?.exp_date) },
    { header: 'MRP', accessor: 'mrp' }
  ].filter(Boolean);

  useEffect(() => {
    let total_qty = 0;
    let total_amt = 0;
    let disc_amt = 0;
    let net_amt = 0;
    let gst_amt = 0;

    detailList.forEach((detail) => {
      total_qty += detail.rec_qty;
      total_amt += detail.amount;
      disc_amt += detail.disc_amt;
      gst_amt += detail.gst_amt;
      net_amt += detail.net_amt;
    });

    // Round net_amt to the nearest integer
    const roundedNetAmt = Math.round(net_amt);

    // Calculate the round off difference (original net_amt - rounded net_amt)
    const roundOff = parseFloat((net_amt - roundedNetAmt).toFixed(2));

    // Set the calculated values
    setValue('total_qty', total_qty);
    setValue('total_amt', total_amt);
    setValue('disc_amt', disc_amt);
    setValue('gst_amt', gst_amt);
    setValue('net_amt', roundedNetAmt); // Set the rounded value as net_amt
    setValue('round_off', Math.abs(roundOff)); // Set the absolute value of the round off difference
  }, [detailList, setValue]);

  const netAmtWatch = watch('bill.net_amt');
  const recAmt1Watch = watch('bill.rec_amt_1');
  const recAmt2Watch = watch('bill.rec_amt_2');
  const payMode = watch('bill.pay_mode');

  useEffect(() => {
    const total_recpt = +recAmt1Watch + +recAmt2Watch;
    let balanceAmt = netAmtWatch - total_recpt;
    if (payMode === 'CASH') {
      setValue('bill.rec_amt_1', netAmtWatch);
      setValue('bill.rec_amt_2', 0);
      setValue('bill.balance_amt', 0);
      setValue('bill.total_recpt', netAmtWatch);
    } else if (['Bank', 'UPI', 'Paytm', 'RTGS/NEFT/Cheque', 'Credit Card', 'Debit Card'].includes(payMode)) {
      setValue('bill.rec_amt_1', 0);
      setValue('bill.rec_amt_2', netAmtWatch);
      setValue('bill.balance_amt', 0);
      setValue('bill.total_recpt', netAmtWatch);
    } else {
      setValue('bill.total_recpt', total_recpt);
      setValue('bill.balance_amt', balanceAmt);
    }
  }, [netAmtWatch, recAmt1Watch, recAmt2Watch, payMode, setValue]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'add_details',
        'uhid'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setDetailList(selectedRecord.add_details);
      const formattedCrnDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('date', formattedCrnDate);
      setValue('from_location', selectedRecord?.from_location?._id);
      setValue('to_location', selectedRecord?.to_location?._id);
      setValue('product_type', selectedRecord?.product_type);
      setValue('invoice_from', selectedRecord?.invoice_from);
      setValue('remarks', selectedRecord?.remarks);
      setValue('to_org', selectedRecord?.to_org);
      setValue('indent_no', selectedRecord?.indent_no);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const getAccountLedgerData = useCallback(async () => {
    const data = await dropdownFetch('account-ledger');
    setAccountLedger(data);
  }, []);

  const getWardData = useCallback(async () => {
    const data = await dropdownFetch('ward');
    setWardDropdown(data);
  }, []);

  useEffect(() => {
    getAccountLedgerData();
    getWardData();
  }, [getAccountLedgerData, getWardData]);

  const getProductDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('stock-master');
      const uniqueProductMap = new Map();
      const filteredProducts = data.flatMap((item) =>
        item.productDetails
          .filter((detail) => {
            const productId = detail.product._id;
            if (!uniqueProductMap.has(productId)) {
              uniqueProductMap.set(productId, true);
              return true;
            }
            return false;
          })
          .map((detail) => ({
            ...detail,
            _id: item._id,
            createdBy: item.createdBy,
            CreatedByDevice: item.CreatedByDevice,
            createdAt: item.createdAt
          }))
      );

      setProductDropdown(filteredProducts);
      setFilteredServiceList(filteredProducts);
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }, []);

  const getProductDropdowns = useCallback(async () => {
    try {
      const data = await dropdownFetch('product-master');
      setProductDropdowns(data);
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }, []);

  const getStoreProductDropdown = useCallback(async () => {
    try {
      const data = await dropdownFetch('store-stock-master'); // Fetch the data
      const uniqueProductMap = new Map(); // To track unique products
      const filteredStoreProducts = data
        .filter((item) => {
          const productId = item.product._id; // Get the product ID from the product field
          if (!uniqueProductMap.has(productId)) {
            uniqueProductMap.set(productId, true); // Mark product as seen
            return true; // Keep the product if it's unique
          }
          return false; // Skip duplicate products
        })
        .map((item) => ({
          _id: item._id, // Store stock master _id
          createdBy: item.createdBy, // createdBy
          createdByDevice: item.createdByDevice, // createdByDevice
          createdAt: item.createdAt, // createdAt
          batch_no: item.batch_no, // Batch number from item
          bill_qty: item.bill_qty, // Bill quantity from item
          total_qty: item.total_qty, // Total quantity from item
          box_qty: item.box_qty, // Box quantity from item
          gst: item.gst, // GST from item
          exp_date: item.exp_date, // Expiration date from item
          mfg_date: item.mfg_date, // Manufacturing date from item
          product: {
            _id: item.product._id, // Product _id
            name: item.product.name, // Product name
            code_alias: item.product.code_alias, // Product alias
            packing: item.product.packing, // Packing
            rate: item.product.rate, // Rate
            product_type: item.product.product_type, // Product type
            status: item.product.status // Product status
          }
        }));
      setStoreProductDropdown(filteredStoreProducts); // Set the filtered products
    } catch (error) {
      console.error('Error fetching store product data:', error);
    }
  }, []);

  useEffect(() => {
    getProductDropdown();
    getStoreProductDropdown();
    getProductDropdowns();
  }, [getProductDropdown, getStoreProductDropdown, getProductDropdowns]);

  const formSubmit = async (formValues) => {
    if (detailList.length === 0) {
      toast('Please add the Details first', { variant: 'warning' });
      return;
    }
    setLoading(true);
    const payload = { ...formValues, add_details: detailList };
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/warehouse-stock-request-update' : '/warehouse-stock-request-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...payload,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success && response.data) {
      toast(`Successfully ${editFlag ? 'edited' : 'created'} Warehouse Stock Request`, { variant: 'success' });
      setSelectedRecord(response?.data);
      setCurrentAction();
      setDetailList([]);
    } else {
      toast(`Failed to ${editFlag ? 'edit' : 'create'} Warehouse Stock Request`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setDetailList((prev) =>
      prev.filter((data) => {
        return data.product !== rowId;
      })
    );
  };

  const onDetailAddHandler = (data) => {
    console.log(data, 'HELLO DATA IS HERE');
    const existingDetail = detailList.find(
      ({ product, store_product }) => product === data.product && store_product === data.store_product
    );
    if (existingDetail) {
      toast('This Product Is Already Chosen', { variant: 'error' });
      return;
    } else {
      toast('Product Added Successfully', { variant: 'success' });
    }
    const productName =
      productDropdowns.find(({ _id }) => _id === data.product)?.main?.name ||
      storeProductDropdown.find(({ product }) => product._id === data.store_product)?.product?.name;

    setDetailList((prev) => [
      ...prev,
      {
        ...data,
        product_name: productName
      }
    ]);
  };
  useEffect(() => {
    console.log(storeProductDropdown, 'HELLO STORE PRODUCT DROPDOWN');
  }, [storeProductDropdown]);

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/warehouse-stock-request-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully Cancel Warehouse Stock Request', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to Cancel Warehouse Stock Request', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/warehouse-stock-request-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Warehouse Stock Request ', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Warehouse Stock Request ', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setWarehouseStockTransferRequestDataObj({ loading: true });
    const response = await request('/warehouse-stock-request-list', { method: 'GET' });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const filteredData = sortedData.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setWarehouseStockTransferRequestDataObj({ loading: false, data: filteredData });
      setOriginalData(filteredData);
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setWarehouseStockTransferRequestDataObj({ loading: false });
    }
  };

  useEffect(() => {
    if (warehouseStockTransferRequestDataObj.data && warehouseStockTransferRequestDataObj.data.length > 0) {
      setOriginalData(warehouseStockTransferRequestDataObj.data);
      setIsFiltered(false);
    }
  }, [warehouseStockTransferRequestDataObj.data]);

  useEffect(() => {
    handleSearch(input);
  }, [input]);
  const handleSearch = async () => {
    const lowercasedInput = input.toUpperCase().trim();
    const limit = 10;
    try {
      const response = await request('/warehouse-stock-request-list', {
        method: 'GET',
        query: { name: lowercasedInput, limit: limit }
      });
      if (response.success) {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setWarehouseStockTransferRequestDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
        setWarehouseStockTransferRequestDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setWarehouseStockTransferRequestDataObj({ loading: false, data: [] });
    }
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  const getWarehouseDropdown = useCallback(async () => {
    const data = await dropdownFetch('warehouse');
    if (!selectedRecord) {
      const [defaultState] = data.filter(({ status }) => status === 'default');
      setValue('location', defaultState?._id);
    }
    setWareHosue(data);
  }, []);

  useEffect(() => {
    getWarehouseDropdown();
  }, [getWarehouseDropdown]);
  const summary = watch('show_summary');

  const productTypeWatch = watch('product_type');
  console.log(detailList, 'HELLO DETAIL LIST');

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Warehouse Stock Transfer Request
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                <Stack sx={{ flex: 1 / 2 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Indent No.</Typography>
                  <TextField sx={{ flex: 0.3 }} name="indent_no" disabled={true} />
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Date</Typography>
                  <TextField
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.3 }}
                    name="date"
                    type="date"
                  />
                </Stack>
                <Stack sx={{ flex: 1 / 2 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.22, fontWeight: 700, fontSize: '1.4rem' }}>To Org</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.8 }}
                    name="to_org"
                    placeholder="Type"
                    menus={PHARMACY_TYPE_MENUS}
                  />
                </Stack>
              </Stack>
              <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                <Stack sx={{ flex: 1 / 2 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Location</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.3 }}
                    name="from_location"
                    menus={warehouse.map((data) => ({ ...data, name: data.warehouse_name }))}
                  />
                  <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Location</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.3 }}
                    name="to_location"
                    menus={warehouse.map((data) => ({ ...data, name: data.warehouse_name }))}
                  />
                </Stack>
                <Stack sx={{ flex: 1 / 2 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.23, fontWeight: 700, fontSize: '1.4rem' }}>Remarks</Typography>
                  <TextField
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="remarks"
                    placeholder="Remarks"
                  />
                </Stack>
              </Stack>

              <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                <Stack sx={{ flex: 1 / 2 }} spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.19, fontWeight: 700, fontSize: '1.4rem' }}>Product Type</Typography>
                  <SelectTag
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.8 }}
                    name="product_type"
                    menus={PHARMACY_PRODUCT_TYPE}
                    placeholder="Select Product Type"
                  />
                </Stack>
              </Stack>
            </Box>

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, boxShadow: '0px 2px 2px #0099ff' }}
            >
              <Stack spacing={1} direction="row" justifyContent="space-between">
                <Button
                  variant="contained"
                  disabled={!['new', 'edit'].includes(currentAction) || !productTypeWatch}
                  onClick={() => setAddDetailsDialogOpen(true)}
                >
                  Add Details
                </Button>
                <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 2 }}>
                  <Checkbox
                    formControlSX={{ flex: 0.8 }}
                    name="view_column"
                    disabled={!['new', 'edit'].includes(currentAction)}
                    fontSize={18}
                  />
                  <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>
                    View All Columns in Table
                  </Typography>
                </Stack>
              </Stack>
              <Table
                containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                data={(detailList && detailList.map((data) => ({ _id: data.product, ...data }))) || []}
                columns={BILL_TABLE_HEADER}
                size="small"
                showDeleteAction={true}
                onDeleteHandler={handleRowDelete}
              />
            </Grid>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Warehouse Stock Transfer Request Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' // Adjust input font size
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Consignment Receipt"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={warehouseStockTransferRequestDataObj?.data || []}
          loading={warehouseStockTransferRequestDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        open={addDetailsDialogue}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '80%', height: '70vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <WarehouseStockTransferRequestDetailForm
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={onDetailAddHandler}
          productDropdown={productDropdown}
          setFilteredServiceList={setFilteredServiceList}
          filteredServiceList={filteredServiceList}
          productType={productTypeWatch}
          setProductDropdown={setProductDropdown}
          setStoreProductDropdown={setStoreProductDropdown}
        />
      </Dialog>
    </>
  );
};
